import {
  initializeFaro, ErrorsInstrumentation, WebVitalsInstrumentation,
  SessionInstrumentation, ViewInstrumentation, NavigationInstrumentation,
  ConsoleInstrumentation, PerformanceInstrumentation, LogLevel, type Faro,
} from "@grafana/faro-web-sdk";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import { propagation, trace, ROOT_CONTEXT, SpanKind, SpanStatusCode } from "@opentelemetry/api";
import { W3CTraceContextPropagator } from "@opentelemetry/core";
import { sanitizeTelemetry } from "./sanitize";

let client: Faro | undefined;

/** An empty URL disables collection, including in ordinary development/tests. */
export function startTelemetry(): boolean {
  const url = import.meta.env.VITE_TELEMETRY_URL;
  if (!url || client) return !!client;
  try {
    const origin = import.meta.env.VITE_API_ORIGIN || window.location.origin;
    const apiPattern = new RegExp(`^${origin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}/api/`);
    client = initializeFaro({
      url,
      app: { name: "airel-web", version: import.meta.env.VITE_APP_VERSION || "local", environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE },
      preventGlobalExposure: true,
      sessionTracking: { persistent: false, samplingRate: 1 },
      consoleInstrumentation: { disabledLevels: [LogLevel.DEBUG, LogLevel.TRACE, LogLevel.LOG, LogLevel.INFO] },
      // Explicit list: don't collect button text or other message content.
      instrumentations: [
        new ErrorsInstrumentation(), new WebVitalsInstrumentation(), new SessionInstrumentation(),
        new ViewInstrumentation(), new NavigationInstrumentation(), new ConsoleInstrumentation(),
        new PerformanceInstrumentation(),
        new TracingInstrumentation({
          propagator: new W3CTraceContextPropagator(),
          resourceAttributes: { "service.namespace": "airel", "deployment.environment.name": import.meta.env.VITE_APP_ENV || import.meta.env.MODE },
          instrumentationOptions: { propagateTraceHeaderCorsUrls: [apiPattern] },
        }),
      ],
      beforeSend: (item) => sanitizeTelemetry(item),
    });
    return true;
  } catch {
    // A failed diagnostics SDK must not prevent login, calls or rendering.
    return false;
  }
}

export function telemetryEvent(name: string, attributes: Record<string, string> = {}): void {
  client?.api.pushEvent(name, attributes, "airel");
}

export function telemetryMeasurement(type: string, values: Record<string, number>, labels: Record<string, string> = {}): void {
  const finite = Object.fromEntries(Object.entries(values).filter(([, value]) => Number.isFinite(value)));
  if (Object.keys(finite).length) client?.api.pushMeasurement({ type, values: finite, context: labels });
}

/** Continue the sender's trace in the receiving browser, without message bodies. */
export function receiveRealtime(carrier: Record<string, string> | undefined, name: string, handle: () => void): void {
  if (!client) { handle(); return; }
  const parent = propagation.extract(ROOT_CONTEXT, carrier ?? {});
  trace.getTracer("airel.realtime").startActiveSpan("realtime.dispatch", {
    kind: SpanKind.CONSUMER,
    attributes: { "messaging.system": "signalr", "messaging.operation.name": name },
  }, parent, (span) => {
    try {
      handle();
      telemetryEvent("realtime.handled", { operation: name, trace_id: span.spanContext().traceId });
    } catch (error) {
      span.setStatus({ code: SpanStatusCode.ERROR });
      if (error instanceof Error) span.recordException(error);
      throw error;
    } finally { span.end(); }
  });
}
